import { isEmpty, lowerCase } from "lodash";
import fp from "lodash/fp";

import { TypeOfBusinessValues, TypeOfLocationValues } from "types";

import { lowerCaseNormalizer } from "./normalizers";

const PROCESSOR_WARNING =
	"Changing a processor will lead to further deletion of the details submitted on software options tab of this application as back end platform will be changed.";

const ONLY_BORDER_ERROR = "onlyBorderError";

const ERROR_BUSINESS_ADDRESS = {
	address_line1: ONLY_BORDER_ERROR,
	address_line2: ONLY_BORDER_ERROR,
	country_code: ONLY_BORDER_ERROR,
	city: ONLY_BORDER_ERROR,
	postal_code: ONLY_BORDER_ERROR,
	region_code: ONLY_BORDER_ERROR,
};

const ERROR_BUSINESS_ADDRESS_WITHOUT_SECOND_ADDRESS = {
	address_line1: ONLY_BORDER_ERROR,
	country_code: ONLY_BORDER_ERROR,
	city: ONLY_BORDER_ERROR,
	postal_code: ONLY_BORDER_ERROR,
	region_code: ONLY_BORDER_ERROR,
};

const ERROR_OWNER_ADDRESS = {
	address_line1: ONLY_BORDER_ERROR,
	address_line2: ONLY_BORDER_ERROR,
	city: ONLY_BORDER_ERROR,
	country_code: ONLY_BORDER_ERROR,
	postal_code: ONLY_BORDER_ERROR,
	region_code: ONLY_BORDER_ERROR,
};

const ERROR_OWNER_ADDRESS_WITHOUT_SECOND_ADDRESS = {
	address_line1: ONLY_BORDER_ERROR,
	city: ONLY_BORDER_ERROR,
	country_code: ONLY_BORDER_ERROR,
	postal_code: ONLY_BORDER_ERROR,
	region_code: ONLY_BORDER_ERROR,
};

export const noActiveAgents = () => {
	return "No active agents";
};

export const getStartedWarn = values => {
	const warnings = {};
	let result = { ...warnings };

	if (values?.processorSponsorSection?.processor?.id) {
		const processorSponsorSection = {
			processor: PROCESSOR_WARNING,
		};
		result = { ...warnings, processorSponsorSection };
	}

	return result;
};

export const businessInfoWarn = (values, { banks, billingBanks }) => {
	const warnings = {};

	if (values.typeOfBusiness === "SoleProp") {
		warnings.legalName = `Must match owner's full name on IRS records`;
	}

	if (values.routing && fp.isEmpty(banks)) {
		warnings.routing = "Routing does not match our records";
	}

	if (values?.billingRouting && fp.isEmpty(billingBanks)) {
		warnings.billingRouting = "Routing does not match our records";
	}

	if (values?.bank && !fp.isEmpty(banks)) {
		const bankExistsWithName = fp.find(["bank", values.bank], banks);

		if (!bankExistsWithName) {
			warnings.bank = "Bank does not match our records";
		}
	}

	if (
		values.monthlyVMCD &&
		values.avgTicketVMCD &&
		parseFloatWithComma(values.avgTicketVMCD) > 0
	) {
		let checkedValue =
			parseFloatWithComma(values.monthlyVMCD) /
			parseFloatWithComma(values.avgTicketVMCD);
		warnings.avgTicketVMCD = `It looks like you process about ${Math.ceil(
			checkedValue,
		)} V/MC/D Transactions per month`;
	}

	if (
		values.monthlyAMEX &&
		values.avgTicketAMEX &&
		parseFloatWithComma(values.avgTicketAMEX) > 0
	) {
		let checkedValue =
			parseFloatWithComma(values.monthlyAMEX) /
			parseFloatWithComma(values.avgTicketAMEX);
		warnings.avgTicketAMEX = `It looks like you process about ${Math.ceil(
			checkedValue,
		)} Amex Transactions per month`;
	}

	const irsName = "Only if different from legal name";

	let result = { ...warnings, irsName };

	return result;
};

const parseFloatWithComma = val => {
	if (isNaN(val)) {
		if (!val) {
			return parseFloat(val);
		}
		val = val.replaceAll(",", "");
	}
	return parseFloat(val);
};

export const businessInfoValidation = values => {
	const errors = {};

	//use 0 for preventing NaN
	let swiped = parseFloat(values.swiped || 0);
	let commerse = parseFloat(values.eCommerce || 0);
	let keyed = parseFloat(values.keyed || 0);

	let sumCards = swiped + commerse + keyed;

	if (sumCards !== 100) {
		if (keyed) errors.keyed = `Values must add up to 100%`;
		if (swiped) errors.swiped = `Values must add up to 100%`;
		if (commerse) errors.eCommerce = `Values must add up to 100%`;
	}

	if (
		!values.monthlyVMCD &&
		!values.monthlyAMEX &&
		!values.avgTicketVMCD &&
		!values.avgTicketAMEX &&
		!values.highTicketVMCD &&
		!values.highTicketAMEX
	) {
		errors.monthlyVMCD = "Field is required";
		errors.monthlyAMEX = "Field is required";
		errors.avgTicketVMCD = "Field is required";
		errors.avgTicketAMEX = "Field is required";
		errors.highTicketVMCD = "Field is required";
		errors.highTicketAMEX = "Field is required";
	}

	return errors;
};

const businessAddressErrorResult = errors => {
	const errorsResult = isEmpty(errors)
		? {}
		: {
				businessAddress: {
					address_line1: errors?.businessAddress?.address_line1,
					address_line2: errors?.businessAddress?.address_line2,
					country_code: errors?.businessAddress?.country_code,
					city: errors?.businessAddress?.city,
					postal_code: errors?.businessAddress?.postal_code,
					region_code: errors?.businessAddress?.region_code,
				},
		  };
	return errorsResult;
};

export const businessLocationAddressValidation = (values, ownerFormValue) => {
	const errors = {};

	const isSoleProprietorTypeOfBusiness =
		values?.typeOfBusiness === TypeOfBusinessValues.SoleProp.name;

	const isHomeBusinessLocation =
		values?.businessAddress?.type_of_location ===
		TypeOfLocationValues.Home.name;

	const isFullBusinessAddresses =
		!isEmpty(values?.businessAddress?.address_line1) &&
		!isEmpty(values?.businessAddress?.city) &&
		!isEmpty(values?.businessAddress?.country_code?.code) &&
		!isEmpty(values?.businessAddress?.postal_code) &&
		!isEmpty(values?.businessAddress?.region_code);

	if (
		isSoleProprietorTypeOfBusiness &&
		!isHomeBusinessLocation &&
		isFullBusinessAddresses
	) {
		// eslint-disable-next-line array-callback-return
		ownerFormValue?.ownerPrincipalSection?.owners?.map(owner => {
			const isSameFullBusinessAndOwnerAddresses =
				lowerCaseNormalizer(values?.businessAddress?.address_line1) ===
					lowerCaseNormalizer(owner?.address_line1) &&
				lowerCaseNormalizer(values?.businessAddress?.address_line2) ===
					lowerCaseNormalizer(owner?.address_line2) &&
				!isEmpty(values?.businessAddress?.address_line2) &&
				!isEmpty(owner?.address_line2) &&
				lowerCaseNormalizer(values?.businessAddress?.city) ===
					lowerCaseNormalizer(owner?.city) &&
				lowerCaseNormalizer(
					values?.businessAddress?.country_code?.code,
				) === lowerCaseNormalizer(owner?.country_code?.code) &&
				lowerCaseNormalizer(values?.businessAddress?.postal_code) ===
					lowerCaseNormalizer(owner?.postal_code) &&
				lowerCaseNormalizer(values?.businessAddress?.region_code) ===
					lowerCaseNormalizer(owner?.region_code);

			const isSameBusinessAndOwnerAddressesWithoutSecondAddresses =
				lowerCaseNormalizer(values?.businessAddress?.address_line1) ===
					lowerCaseNormalizer(owner?.address_line1) &&
				isEmpty(values?.businessAddress?.address_line2) &&
				isEmpty(owner?.address_line2) &&
				lowerCaseNormalizer(values?.businessAddress?.city) ===
					lowerCaseNormalizer(owner?.city) &&
				lowerCaseNormalizer(
					values?.businessAddress?.country_code?.code,
				) === lowerCaseNormalizer(owner?.country_code?.code) &&
				lowerCaseNormalizer(values?.businessAddress?.postal_code) ===
					lowerCaseNormalizer(owner?.postal_code) &&
				lowerCaseNormalizer(values?.businessAddress?.region_code) ===
					lowerCaseNormalizer(owner?.region_code);

			if (isSameBusinessAndOwnerAddressesWithoutSecondAddresses) {
				errors.businessAddress =
					ERROR_BUSINESS_ADDRESS_WITHOUT_SECOND_ADDRESS;
			}

			if (isSameFullBusinessAndOwnerAddresses) {
				errors.businessAddress = ERROR_BUSINESS_ADDRESS;
			}
		});
	}

	return businessAddressErrorResult(errors);
};

const mpaAddressErrorResult = errors => {
	const ownersAddressesErrors = errors?.ownerPrincipalSection?.owners.map(
		item => {
			return {
				address_line1: item.address_line1,
				address_line2: item.address_line2,
				city: item.city,
				country_code: item.country_code,
				postal_code: item.postal_code,
				region_code: item.region_code,
			};
		},
	);

	const errorsResult = isEmpty(errors)
		? {}
		: {
				businessAddress: {
					address_line1: errors?.businessAddress?.address_line1,
					address_line2: errors?.businessAddress?.address_line2,
					country_code: errors?.businessAddress?.country_code,
					city: errors?.businessAddress?.city,
					postal_code: errors?.businessAddress?.postal_code,
					region_code: errors?.businessAddress?.region_code,
				},
				ownerPrincipalSection: {
					owners: ownersAddressesErrors,
				},
		  };

	return errorsResult;
};

const ownerAddressErrorResult = errors => {
	const ownersAddressesErrors = errors?.ownerPrincipalSection?.owners.map(
		item => {
			return {
				address_line1: item.address_line1,
				address_line2: item.address_line2,
				city: item.city,
				country_code: item.country_code,
				postal_code: item.postal_code,
				region_code: item.region_code,
			};
		},
	);

	const errorsResult = isEmpty(errors)
		? {}
		: {
				ownerPrincipalSection: {
					owners: ownersAddressesErrors,
				},
		  };

	return errorsResult;
};

export const principalInfoValidation = (values, businessFormValue) => {
	const errors = {};

	const isSoleProprietorTypeOfBusiness =
		businessFormValue?.typeOfBusiness ===
		TypeOfBusinessValues.SoleProp.name;
	const isHomeBusinessLocation =
		businessFormValue?.businessAddress?.type_of_location ===
		TypeOfLocationValues.Home.name;

	if (
		isSoleProprietorTypeOfBusiness &&
		!isHomeBusinessLocation &&
		!isEmpty(businessFormValue?.businessAddress?.type_of_location)
	) {
		// eslint-disable-next-line array-callback-return
		values?.ownerPrincipalSection?.owners?.map((owner, index) => {
			const isSameFullBusinessAndOwnerAddresses =
				lowerCase(businessFormValue?.businessAddress?.address_line1) ===
					lowerCase(owner?.region_code) &&
				!isEmpty(businessFormValue?.businessAddress?.address_line2) &&
				!isEmpty(owner?.address_line2) &&
				lowerCase(businessFormValue?.businessAddress?.address_line2) ===
					lowerCase(owner?.address_line2) &&
				lowerCase(businessFormValue?.businessAddress?.city) ===
					lowerCase(owner?.city) &&
				lowerCaseNormalizer(
					businessFormValue?.businessAddress?.country_code?.code,
				) === lowerCaseNormalizer(owner?.country_code?.code) &&
				lowerCaseNormalizer(
					businessFormValue?.businessAddress?.postal_code,
				) === lowerCaseNormalizer(owner?.postal_code) &&
				lowerCaseNormalizer(
					businessFormValue?.businessAddress?.region_code,
				) === lowerCaseNormalizer(owner?.region_code);

			const isSameBusinessAndOwnerAddressesWithoutSecondAddresses =
				!isEmpty(businessFormValue?.businessAddress?.address_line1) &&
				!isEmpty(owner?.address_line1) &&
				lowerCaseNormalizer(
					businessFormValue?.businessAddress?.address_line1,
				) === lowerCaseNormalizer(owner?.address_line1) &&
				isEmpty(businessFormValue?.businessAddress?.address_line2) &&
				isEmpty(owner?.address_line2) &&
				lowerCase(businessFormValue?.businessAddress?.city) ===
					lowerCase(owner?.city) &&
				!isEmpty(
					businessFormValue?.businessAddress?.country_code?.code,
				) &&
				!isEmpty(owner?.country_code?.code) &&
				lowerCase(
					businessFormValue?.businessAddress?.country_code?.code,
				) === lowerCase(owner?.country_code?.code) &&
				!isEmpty(businessFormValue?.businessAddress?.postal_code) &&
				!isEmpty(owner?.postal_code) &&
				lowerCaseNormalizer(
					businessFormValue?.businessAddress?.postal_code,
				) === lowerCaseNormalizer(owner?.postal_code) &&
				!isEmpty(businessFormValue?.businessAddress?.region_code) &&
				!isEmpty(owner?.region_code) &&
				lowerCaseNormalizer(
					businessFormValue?.businessAddress?.region_code,
				) === lowerCaseNormalizer(owner?.region_code);

			if (isSameBusinessAndOwnerAddressesWithoutSecondAddresses) {
				if (isEmpty(errors?.ownerPrincipalSection?.owners)) {
					errors.ownerPrincipalSection = {};
					errors.ownerPrincipalSection.owners = [];
					errors.ownerPrincipalSection.owners[index] =
						ERROR_OWNER_ADDRESS_WITHOUT_SECOND_ADDRESS;
				} else {
					errors.ownerPrincipalSection.owners.push(
						ERROR_OWNER_ADDRESS_WITHOUT_SECOND_ADDRESS,
					);
				}
			}

			if (isSameFullBusinessAndOwnerAddresses) {
				if (isEmpty(errors?.ownerPrincipalSection?.owners)) {
					errors.ownerPrincipalSection = {};
					errors.ownerPrincipalSection.owners = [];
					errors.ownerPrincipalSection.owners[index] =
						ERROR_OWNER_ADDRESS;
				} else {
					errors.ownerPrincipalSection.owners.push(
						ERROR_OWNER_ADDRESS,
					);
				}
			}
		});
	}

	return ownerAddressErrorResult(errors);
};

export const mpaUploadValidation = (values, formName, dispatch, touch) => {
	const errors = {};

	const isSoleProprietorTypeOfBusiness =
		values?.typeOfBusiness === TypeOfBusinessValues.SoleProp.name;
	const isHomeBusinessLocation =
		values?.businessAddress?.type_of_location ===
		TypeOfLocationValues.Home.name;

	const businessAddressesFieldNames = [
		"address_line1",
		"address_line2",
		"country_code",
		"city",
		"postal_code",
		"region_code",
	];

	const ownerAddressFieldNames = [
		"address_line1",
		"address_line2",
		"country_code",
		"city",
		"postal_code",
		"region_code",
	];

	const isFullBusinessAddresses =
		!isEmpty(values?.businessAddress?.address_line1) &&
		!isEmpty(values?.businessAddress?.city) &&
		!isEmpty(values?.businessAddress?.country_code?.code) &&
		!isEmpty(values?.businessAddress?.postal_code) &&
		!isEmpty(values?.businessAddress?.region_code);

	if (
		isSoleProprietorTypeOfBusiness &&
		!isHomeBusinessLocation &&
		isFullBusinessAddresses
	) {
		// eslint-disable-next-line array-callback-return
		values?.ownerPrincipalSection?.owners?.map((owner, index) => {
			const isSameFullBusinessAndOwnerAddresses =
				lowerCaseNormalizer(values?.businessAddress?.address_line1) ===
					lowerCaseNormalizer(owner?.address_line1) &&
				lowerCaseNormalizer(values?.businessAddress?.address_line2) ===
					lowerCaseNormalizer(owner?.address_line2) &&
				!isEmpty(values?.businessAddress?.address_line2) &&
				!isEmpty(owner?.address_line2) &&
				lowerCaseNormalizer(values?.businessAddress?.city) ===
					lowerCaseNormalizer(owner?.city) &&
				lowerCaseNormalizer(
					values?.businessAddress?.country_code?.code,
				) === lowerCaseNormalizer(owner?.country_code?.code) &&
				lowerCaseNormalizer(values?.businessAddress?.postal_code) ===
					lowerCaseNormalizer(owner?.postal_code) &&
				lowerCaseNormalizer(values?.businessAddress?.region_code) ===
					lowerCaseNormalizer(owner?.region_code);

			const isSameBusinessAndOwnerAddressesWithoutSecondAddresses =
				lowerCaseNormalizer(values?.businessAddress?.address_line1) ===
					lowerCaseNormalizer(owner?.address_line1) &&
				isEmpty(values?.businessAddress?.address_line2) &&
				isEmpty(owner?.address_line2) &&
				lowerCaseNormalizer(values?.businessAddress?.city) ===
					lowerCaseNormalizer(owner?.city) &&
				lowerCaseNormalizer(
					values?.businessAddress?.country_code?.code,
				) === lowerCaseNormalizer(owner?.country_code?.code) &&
				lowerCaseNormalizer(values?.businessAddress?.postal_code) ===
					lowerCaseNormalizer(owner?.postal_code) &&
				lowerCaseNormalizer(values?.businessAddress?.region_code) ===
					lowerCaseNormalizer(owner?.region_code);

			const touchOwnerAndBusinessAddressFields = () => {
				for (const addressName of businessAddressesFieldNames) {
					dispatch(touch(formName, `businessAddress.${addressName}`));
				}

				for (const addressName of ownerAddressFieldNames) {
					dispatch(
						touch(
							formName,
							`${"ownerPrincipalSection"}.${"owners"}.${index}.${addressName}`,
						),
					);
				}
			};

			if (isSameBusinessAndOwnerAddressesWithoutSecondAddresses) {
				errors.businessAddress =
					ERROR_BUSINESS_ADDRESS_WITHOUT_SECOND_ADDRESS;

				if (isEmpty(errors?.ownerPrincipalSection?.owners)) {
					errors.ownerPrincipalSection = {};
					errors.ownerPrincipalSection.owners = [];
					errors.ownerPrincipalSection.owners[index] =
						ERROR_OWNER_ADDRESS_WITHOUT_SECOND_ADDRESS;
				} else {
					errors.ownerPrincipalSection.owners.push(
						ERROR_OWNER_ADDRESS_WITHOUT_SECOND_ADDRESS,
					);
				}

				touchOwnerAndBusinessAddressFields();
			}

			if (isSameFullBusinessAndOwnerAddresses) {
				errors.businessAddress = ERROR_BUSINESS_ADDRESS;

				if (isEmpty(errors?.ownerPrincipalSection?.owners)) {
					errors.ownerPrincipalSection = {};
					errors.ownerPrincipalSection.owners = [];
					errors.ownerPrincipalSection.owners[index] =
						ERROR_OWNER_ADDRESS;
				} else {
					errors.ownerPrincipalSection.owners.push(
						ERROR_OWNER_ADDRESS,
					);
				}

				touchOwnerAndBusinessAddressFields();
			}
		});
	}

	return mpaAddressErrorResult(errors);
};

export const touchOwnerAddressFields = (
	FORM_NAME,
	formErrors,
	dispatch,
	touch,
) => {
	formErrors?.ownerPrincipalSection?.owners?.forEach((item, index) => {
		for (let name in item) {
			const value = item[name];

			if (value === ONLY_BORDER_ERROR) {
				dispatch(
					touch(
						FORM_NAME,
						`${"ownerPrincipalSection"}.${"owners"}.${index}.${name}`,
					),
				);
			}
		}
	});
};

export const touchBusinessAddressFields = (
	FORM_NAME,
	formErrors,
	dispatch,
	touch,
) => {
	const businessAddressErrors = formErrors?.businessAddress;
	for (let name in businessAddressErrors) {
		const value = businessAddressErrors[name];
		if (value === ONLY_BORDER_ERROR) {
			dispatch(touch(FORM_NAME, `businessAddress.${name}`));
		}
	}
};

export const getOwnerAddressIndex = formErrors => {
	const ownersIndex = [];
	formErrors?.ownerPrincipalSection?.owners?.forEach((item, index) => {
		for (let name in item) {
			const value = item[name];
			if (value === ONLY_BORDER_ERROR) {
				ownersIndex.push(index);
			}
		}
	});
	return [...new Set(ownersIndex)];
};

export const isShowBusinessAddressWarningTooltip = formErrors => {
	if (formErrors?.businessAddress) {
		const errorResult = Object.values(formErrors?.businessAddress).includes(
			ONLY_BORDER_ERROR,
		);

		return errorResult;
	}
};

export const underwritingMerchantInfoWarn = values =>
	values?.processor && { processor: PROCESSOR_WARNING };
